/**
 * Translate raw Swedish material / part names to the active locale.
 *
 * The engine layer continues to produce Swedish strings as stable
 * internal IDs (the price estimator relies on exact matching).
 * This helper is applied at the UI render boundary so that the
 * intl market sees English while the se market remains unchanged.
 */
import { stripSpeciesSuffix } from "../domain/finishes/applyInteriorWood";
import {
	INTERIOR_WOOD_CATALOG,
	readSpeciesSuffix,
} from "../domain/finishes/finishCatalog";
import type { Dictionary } from "./types";

// ─── Material name mapping (MaterialRow.material) ──────────

type DictKey = keyof Dictionary;

const MATERIAL_MAP: Array<[RegExp, DictKey]> = [
	[/^Betongplint/, "term_concretePier"],
	[/^Bärlina Tryckimpregnerad/, "term_treatedBearer"],
	[/^Golv- & Takbjälkar/, "term_floorRoofJoists"],
	[/^Golvbjälkar \(K-virke\)/, "term_floorJoistsCut"],
	[/^Takbjälkar \(K-virke\)/, "term_roofJoistsCut"],
	[/^Takbjälkar \(limträ\)/, "term_roofJoistsGlulam"],
	[/^Väggreglar, Syll/, "term_wallStudsSillPlate"],
	[/^Väggreglar \(Ytter\+Delning\)/, "term_wallStudsDivision"],
	[/^Stenullsisolering/, "term_mineralWoolInsulation"],
	[/^Åldersbeständig Bastufolie/, "term_saunaFoil"],
	[/^(Inv\. )?Spikläkt/, "term_furringStrips"],
	// Kept for designs/snapshots written before issue 134 renamed the row.
	[/^Bastupanel \(t\.ex\./, "term_saunaPanelSpruce"],
	[/^Bastupanel \(Bastudel\)/, "term_saunaPanelAspen"],
	[/^Invändig Panel \(Relaxdel\)/, "term_interiorPanelLounge"],
	[/^Lav-virke/, "term_benchTimber"],
	// Issue 130 bench rows. `translateMaterial` returns the dictionary label
	// alone, so the Swedish parenthetical never survives to intl.
	[/^Lavbärlina/, "term_benchLedger"],
	[/^Lavfront/, "term_benchFrontBoard"],
	[/^Lavstomme/, "term_benchFrame"],
	[/^Lavskruv/, "term_benchScrews"],
	[/^Fukttåligt golv/, "term_moistureResistantFloor"],
	// Issue 174: the indoor sauna's outside. Before the generic panel row.
	[/^Ytterpanel \(Spontad Gran\), fristående/, "term_indoorOuterPanel"],
	[/^Träfiberskiva mot befintlig vägg/, "term_indoorFibreboard"],
	[/^Ytterpanel \(Spontad/, "term_exteriorCladding"],
	[/^Utv\. Ströläkt/, "term_exteriorBattenSpacer"],
	[/^Utv\. Spikläkt/, "term_exteriorFurring"],
	[/^Vindskydd \(skiva\/duk\)/, "term_windBarrierBoard"],
	[/^Knutbrädor/, "term_cornerTrim"],
	[/^Nederkantsavslut/, "term_baseTrim"],
	[/^Vindskivor \(Fascia\)/, "term_fasciaBoard"],
	[/^Vindskivor \/ Fasadavslut/, "term_fasciaBoard"],
	[/^Takfotsavslut/, "term_soffitBoard"],
	[/^Dörr & Tillbehör/, "term_doorAndAccessories"],
	[/^Dörrar & Tillbehör/, "term_doorsAndAccessories"],
	// Must precede /^Undergolv/ only in intent, not in order (different
	// anchors) — but keep it beside the row it belongs to.
	[/^Trossbottenläkt/, "term_subfloorLedger"],
	[/^Undergolv(?: \/ Trossbotten)?/, "term_subfloor"],
	[/^Stavvirke/, "term_staveTimber"],
	[/^Gavlar \(Limträ/, "term_endCaps"],
	[/^Stålband/, "term_steelBands"],
	[/^Vaggor \/ Stöd/, "term_cradles"],
	[/^Golvbrädor \(inre\)/, "term_floorBoards"],
	[/^Bastulav-virke/, "term_benchTimberBarrel"],
	[/^Bastudörr/, "term_saunaDoor"],
	[/^Bastuaggregat/, "term_heaterUnit"],
	// Outside-fired heater extras (issue 159).
	[/^Brandskiva kalciumsilikat/, "term_fireBoardCalciumSilicate"],
	[/^Brandtätning eldningstunnel/, "term_feedTunnelSeal"],
	[/^Ventiler/, "term_vents"],
	// Deck materials
	[/^Trallbräda/, "term_deckBoard"],
	[/^Regel$/, "term_joist"],
	[/^Bärlina$/, "term_bearer"],
	[/^Kantbräda/, "term_deckFascia"],
	[/^Kortling för mellanvägg/, "term_partitionBlocking"],
	[/^Kortling/, "term_nogging"],
	[/^Trallskruv/, "term_deckScrew"],
	// Roof materials
	[/^Råspont/, "term_roofDecking"],
	[/^Underlagspapp/, "term_roofingUnderlay"],
	[/^Ytpapp/, "term_roofingFelt"],
	// Must precede /^Takplåt/-style entries only in intent — the sheet_metal
	// roof system emits "Bandtäckt takplåt", which no anchored ^Takplåt
	// pattern can ever reach.
	[/^Bandtäckt takplåt/, "term_standingSeamRoofing"],
	[/^Takplåt/, "term_sheetMetalRoofing"],
	[/^Takfotsplåt/, "term_eaveFlashing"],
	[/^Vindskiveplåt/, "term_vergeFlashing"],
	[/^Nockbeslag/, "term_ridgeCapping"],
	// Foundation materials
	[/^Stolpsko/, "term_postShoe"],
	[/^Markduk/, "term_groundFabricMat"],
	[/^Dräneringsgrus/, "term_drainageGravel"],
	// System materials
	[/^Alutejp/, "term_aluTapeMat"],
	[/^Rostfri skruv/, "term_stainlessScrews"],
	[/^Ytbehandling/, "term_surfaceTreatment"],
	[/^Styrpanel/, "term_controlPanel"],
	[/^Bastuarmatur/, "term_saunaLightFixture"],
	[/^Kopplingsmaterial/, "term_wiringAccessories"],
	[/^Silikonkabel/, "term_siliconeCable"],
	// === Added: 10 previously unmatched engine materials ===
	[/^Aggregatsten/, "term_heaterStones"],
	[/^Bastupanel \(Innertak bastudel\)/, "term_ceilingPanelSauna"],
	[/^Bastupanel \(Innertak\)/, "term_ceilingPanel"],
	[/^Elaggregat/, "term_electricHeater"],
	[/^Fönster/, "term_window"],
	[/^Invändig panel \(Innertak relaxdel\)/, "term_ceilingPanelLounge"],
	[/^Mellendörr/, "term_interiorDoor"],
	[/^Panoramaglas/, "term_panoramaGlass"],
	[/^Strålskydd/, "term_heatShield"],
	[/^Takgles \/ Spikläkt/, "term_ceilingBattens"],
	[/^Taklist \/ Bomlist/, "term_crownMoulding"],
	[/^Ytterdörr/, "term_exteriorDoor"],
	// === Added: intl coverage — opening trim & panorama framing ===
	[/^Panoramaavväxling/, "term_panoramaHeader"],
	[/^Avväxling/, "term_openingHeader"],
	[/^Dörrfoder/, "term_doorCasing"],
	// === Issue 134: karaktärsval ===
	// The bare `Bastupanel` row (the square engine's, once the "t.ex. Gran/Asp"
	// hedge became a real choice). `$`-anchored so it cannot shadow
	// "Bastupanel (Innertak)" — and the species suffix is stripped before this
	// map runs, so the base is genuinely bare here.
	[/^Bastupanel$/, "term_saunaPanel"],
	[/^Tjärvitriol/, "term_exteriorTreatmentTjarvitriol"],
	[/^Järnvitriol/, "term_exteriorTreatmentJarnvitriol"],
	[/^Slamfärg röd/, "term_exteriorTreatmentSlamfargRed"],
	[/^Slamfärg svart/, "term_exteriorTreatmentSlamfargBlack"],
];

/**
 * Translate a MaterialRow.material string to the active locale.
 * Falls through to the raw string if no match is found.
 */
export function translateMaterial(raw: string, t: Dictionary): string {
	// The species suffix (`"Bastupanel, asp"`) is stripped before the map
	// lookup and re-appended in the target language, so ONE entry per base row
	// serves every wood. Both the pattern and the label come from
	// INTERIOR_WOOD_CATALOG — a copy here is how a fourth wood would ship a
	// Swedish word to intl unnoticed, since the species words are pure ASCII
	// and the å/ä/ö guard is blind to them.
	const species = readSpeciesSuffix(raw);
	const base = species ? stripSpeciesSuffix(raw) : raw;

	let label = base;
	for (const [pattern, key] of MATERIAL_MAP) {
		if (pattern.test(base)) {
			label = t[key] as string;
			break;
		}
	}

	if (!species) return label;
	// Lower-cased so the suffix reads as part of the sentence in both
	// dictionaries ("Bastupanel, asp" / "Sauna panel, aspen") while the swatch
	// UI keeps the same key capitalised.
	const speciesLabel = t[INTERIOR_WOOD_CATALOG[species].labelKey] as string;
	return `${label}, ${speciesLabel.toLowerCase()}`;
}

// ─── Part name mapping (CutRow.part) ───────────────────────

const PART_MAP: Array<[RegExp, DictKey]> = [
	// Cut-list names the material rows spell differently. Both are ASCII-only,
	// so only the expected-string test below can see them leak.
	[/^Innertakspanel/, "term_ceilingPanelCut"],
	[/^Taklist \/ Bomlist/, "term_crownMoulding"],
	[/^King stud/, "cut_kingStud"],
	[/^Jack stud/, "cut_jackStud"],
	[/^Cripple stud/, "cut_crippleStud"],
	[/^Header/, "term_openingHeader"],
	[/^Bärlinor \(Grund/, "term_bearersCut"],
	[/^Golvbjälkar/, "term_floorJoistsCut"],
	[
		/^Takbjälkar \(limträ, dimensioneras av konstruktör\)/,
		"term_roofJoistsGlulamCut",
	],
	[/^Takbjälkar/, "term_roofJoistsCut"],
	[/^Syll \/ Hammarband/, "term_sillPlateCut"],
	[/^Syllbotten/, "term_sillBottomCut"],
	[/^Hammarband/, "term_topPlateCut"],
	[/^Stående Väggreglar \(Framsida/, "term_wallStudsFrontCut"],
	[/^Stående Väggreglar \(Baksida/, "term_wallStudsBackCut"],
	[/^Stående Väggreglar \(Sidor/, "term_wallStudsSidesCut"],
	[/^Vindskivor \(Fram\/Bak/, "term_fasciaFrontBackCut"],
	[/^Vindskivor \/ Fasadavslut/, "term_fasciaEndCut"],
	[/^Vindskivor/, "term_fasciaBoardsCut"],
	[/^Ytterpanel \(Fasad\)/, "term_exteriorCladdingCut"],
	[/^Ytterpanel \(fristående väggar\)/, "term_indoorOuterPanelCut"],
	[/^Knutbrädor/, "term_cornerTrimCut"],
	[/^Nederkantsavslut/, "term_baseTrimCut"],
	[/^Takfotsavslut/, "term_soffitBoardCut"],
	[/^Stavar/, "term_stavesCut"],
	[/^Gavelskivor/, "term_endCapsCut"],
	[/^Stålband/, "term_steelBandsCut"],
	[/^Vaggor/, "term_cradlesCut"],
	[/^Golvbrädor/, "term_floorBoardsCut"],
	[/^Delningsvägg (?:jamb|stud)-/, "term_dividerStudsCut"],
	[/^Delningsvägg (?:header|sill|top-plate)/, "term_dividerSillPlateCut"],
	[/^Delningsvägg Syll/, "term_dividerSillPlateCut"],
	[/^Stående Reglar \(Delningsvägg\)/, "term_dividerStudsCut"],
	[/^Undergolv(?: \/ Trossbotten)?/, "term_subfloorCut"],
	// Reuses the material key: the cut row's leading label is identical to the
	// BOM row's, and the "(28×70)" remainder carries no Swedish characters.
	[/^Trossbottenläkt/, "term_subfloorLedger"],
	// Deck cut-list parts
	[/^Trallbrädor/, "term_deckBoardsCut"],
	[/^Reglar/, "term_joistsCut"],
	[/^Bärlinor/, "term_bearersDeckCut"],
	[/^Kantbrädor/, "term_deckFasciaCut"],
	[/^Kortlingar/, "term_noggingsCut"],
	// === Added: intl coverage — opening trim & panorama framing ===
	// (must stay after /^Kortlingar/ so the plural is not shadowed)
	[/^Dörrfoder/, "term_doorCasing"],
	[/^Fönsterbröstning/, "term_windowApron"],
	[/^Fönsterfoder/, "term_windowCasing"],
	[/^Panoramaavväxling/, "term_panoramaHeader"],
	[/^Panorama överstöd/, "term_panoramaUpperSupport"],
	[/^Panorama understöd/, "term_panoramaLowerSupport"],
	[/^Panoramastöd/, "term_panoramaSupport"],
	[/^Kortling/, "term_nogging"],
	// Issue 130 bench cut rows. The tier ("övre"/"nedre") rides in the
	// remainder and is cleaned by cleanupSwedishPartWords below.
	[/^Ryggstöd distanser/, "term_benchBackrestSpacersCut"],
	[/^Ryggstöd lav/, "term_benchBackrestCut"],
	[/^Lavbrädor/, "term_benchSlatsCut"],
	[/^Lavbärlina/, "term_benchLedgerCut"],
	[/^Lavfront spjälor/, "term_benchSkirtCut"],
	[/^Lavfront/, "term_benchFrontBoard"],
	// The two frame cuts are different lengths, so they are different rows —
	// both must precede the bare /^Lavstomme/ fallback.
	[/^Lavstomme bakregel/, "term_benchBackRailCut"],
	[/^Lavstomme gavlar/, "term_benchEndRailsCut"],
	[/^Lavstomme tvärbalkar/, "term_benchJoistsCut"],
	[/^Lavstomme/, "term_benchFrameCut"],
	[/^Lavben/, "term_benchLegsCut"],
	// Issue 134: the interior panel cut row names its direction, and both
	// "stående" and "liggande" are pure ASCII apart from the å — spell the
	// whole label out so neither can fall through half-translated.
	[/^Bastupanel stående/, "term_cut_interiorPanelStanding"],
	[/^Bastupanel liggande/, "term_cut_interiorPanelLying"],
];

/**
 * Clean up Swedish framing words that survive in a part's parenthetical
 * detail (e.g. "(mellanväggsanslutning)", "höger yttervägg", "(långvägg)").
 * On the se market every key resolves back to the identical Swedish word, so
 * this is a no-op there and only takes effect for the intl dictionary.
 */
function cleanupSwedishPartWords(value: string, t: Dictionary): string {
	return (
		value
			.replace(/\b(?:Långsida|Långledden)\b/gi, t.cut_longSide)
			.replace(/\b(?:Kortsida|Kortledden)\b/gi, t.cut_shortSide)
			.replace(/\bEna ?ledden\b/gi, t.cut_oneDirection)
			.replace(/\bPlan\b/g, t.cut_squareEnd)
			// Bench cut rows carry the room, so both two-room benches are tellable
			// apart on the list.
			.replace(/\(Bastudel\)/g, `(${t.term_qty_saunaSection})`)
			.replace(/\(Relaxdel\)/g, `(${t.term_qty_loungeSection})`)
			.replace(/\bhöger\b/g, t.term_side_right as string)
			.replace(/\bvänster\b/g, t.term_side_left as string)
			.replace(/\byttervägg\b/g, t.term_wall_outer as string)
			.replace(
				/\bmellanväggsanslutning\b/g,
				t.term_partition_junction as string,
			)
			.replace(/\blångvägg\b/g, t.term_wall_long as string)
			// "övre" starts with a non-ASCII letter, and JS `\b` is ASCII-only — it
			// finds no boundary between "(" (or a space) and "ö", so `\bövre\b`
			// silently never matches. Anchor on the surrounding whitespace instead.
			.replace(/(^|\s)övre(?=\s|$)/g, `$1${t.term_bench_upper as string}`)
			.replace(/(^|\s)nedre(?=\s|$)/g, `$1${t.term_bench_lower as string}`)
	);
}

/**
 * Translate a CutRow.part string to the active locale.
 *
 * Many cut-list parts include parenthetical details (dimensions,
 * instructions). The regex matches only the leading label; the
 * remainder of the string is cleaned of residual Swedish framing words.
 */
/**
 * Translate the leading material name of a "<name> <dimension>" label while
 * preserving the dimension.
 *
 * `translateMaterial` returns the dictionary label *alone*, which would drop
 * the dimension, and several MATERIAL_MAP patterns are `$`-anchored
 * (`/^Regel$/`, `/^Bärlina$/`) so they deliberately never match a label that
 * carries a dimension. Matching the first token against the map gives both:
 * the anchors still hold, and the remainder survives.
 */
function translateLeadingMaterialName(raw: string, t: Dictionary): string {
	const parts = raw.match(/^(\S+)(.*)$/);
	if (!parts) return raw;

	const [, name, remainder] = parts;
	for (const [pattern, key] of MATERIAL_MAP) {
		if (pattern.test(name)) {
			return `${t[key] as string}${remainder}`;
		}
	}
	return raw;
}

export function translatePart(raw: string, t: Dictionary): string {
	const species = readSpeciesSuffix(raw);
	if (species) return `${translatePart(stripSpeciesSuffix(raw), t)}, ${(t[INTERIOR_WOOD_CATALOG[species].labelKey] as string).toLowerCase()}`;
	for (const [pattern, key] of PART_MAP) {
		if (pattern.test(raw)) {
			// Replace the matched leading label, keep trailing detail
			const match = raw.match(pattern);
			if (match) {
				const translated = t[key] as string;
				let remainder = raw.slice(match[0].length);
				// A legacy prefix can consume an opening parenthesis (e.g. front
				// wall studs). Rebuild its remaining detail without orphan brackets.
				if (
					(match[0].match(/\(/g)?.length ?? 0) >
					(match[0].match(/\)/g)?.length ?? 0)
				) {
					const closing = remainder.indexOf(")");
					if (closing >= 0) {
						const detail = remainder
							.slice(0, closing)
							.replace(/^[\s:,]+/, "")
							.trim();
						remainder =
							(detail ? ` (${detail})` : "") + remainder.slice(closing + 1);
					}
				}
				return cleanupSwedishPartWords(translated + remainder, t);
			}
		}
	}

	// Handle deck cut-plan rows: "Trallbräda 28×120 mm inköp" → "Deck board 28×120 mm purchase"
	const purchaseMatch = raw.match(/^(.+?)\s+inköp$/);
	if (purchaseMatch) {
		return `${translateLeadingMaterialName(purchaseMatch[1], t)} ${t.term_purchase}`;
	}

	return cleanupSwedishPartWords(raw, t);
}

// ─── Quantity string translation (MaterialRow.quantity) ─────

/**
 * Translate a MaterialRow.quantity string.
 *
 * Handles patterns like: "12 st", "45 lpm", "Vägg: 12m²", "(Bastudel)",
 * "Inkl. 10% spill", "(+ Alu-tejp)", item listings, etc.
 */
export function translateQuantity(raw: string, t: Dictionary): string {
	let result = raw;

	// Units: "st" → pcs, "lpm" → lm
	result = result.replace(/\b(\d+)\s*st\b/g, `$1 ${t.term_unit_pcs}`);
	result = result.replace(/\b(\d+)\s*sats\b/g, `$1 ${t.term_unit_set}`);
	result = result.replace(/\b(\d+)\s*lpm\b/g, `$1 ${t.term_unit_lm}`);
	// "liter" is the facade-treatment row's unit (issue 134). Pure ASCII, so
	// the å/ä/ö sweep would never have flagged it left untranslated.
	result = result.replace(/\b(\d+)\s*liter\b/g, `$1 ${t.term_qty_litre}`);

	// "Vägg:" / "Tak&Golv:" / "Tak:" / "Golv:" labels in insulation qty.
	// Tak&Golv first — its "Tak" prefix must never be eaten by the plain
	// "Tak:" replace ("Tak&" has no colon, but keep the safe order anyway).
	result = result.replace(/\bVägg:/g, `${t.term_qty_wall}:`);
	result = result.replace(/\bTak&Golv:/g, `${t.term_qty_roofFloor}:`);
	result = result.replace(/\bTak:/g, `${t.term_qty_roof}:`);
	result = result.replace(/\bGolv:/g, `${t.term_qty_floor}:`);

	// "(Bastudel)" / "(Relaxdel)" section markers
	result = result.replace(/\(Bastudel\)/g, `(${t.term_qty_saunaSection})`);
	result = result.replace(/\(Relaxdel\)/g, `(${t.term_qty_loungeSection})`);

	// "Inkl. 10% spill" → "Incl. 10% waste"
	result = result.replace(
		/Inkl\.\s*(\d+%?)\s*spill/g,
		`${t.term_qty_incl} $1 ${t.term_waste}`,
	);

	// "(+ Alu-tejp)" → "(+ alu tape)"
	result = result.replace(/\(\+\s*Alu-tejp\)/g, `(+ ${t.term_qty_aluTape})`);

	// Accessory listing: "1 Bastudörr, 2 Ventiler, 1 Aggregat, Skruv"
	result = result.replace(/\bBastudörr\b/g, t.term_qty_saunaDoor as string);
	result = result.replace(/\bYtterdörr\b/g, t.term_qty_exteriorDoor as string);
	result = result.replace(/\bMellendörr\b/g, t.term_qty_interiorDoor as string);
	result = result.replace(/\bVentiler\b/g, t.term_qty_vents as string);
	result = result.replace(/\bAggregat\b/g, t.term_qty_heaterUnit as string);
	result = result.replace(/\bSkruv\b/g, t.term_qty_screws as string);
	result = result.replace(/\bFönster\b/g, t.term_qty_window as string);

	// "Vedeldad rekommenderas" → "wood-burning recommended"
	result = result.replace(
		/Vedeldad rekommenderas/g,
		t.term_qty_woodRecommended as string,
	);

	// "Tilluft + Frånluft" → "supply + exhaust"
	result = result.replace(
		/Tilluft \+ Frånluft/g,
		t.term_qty_supplyExhaust as string,
	);

	// "ca " → "approx. " (Swedish "circa")
	result = result.replace(/\bca\s/g, `${t.term_qty_approx} `);

	// Unit: "paket" → "pack"
	result = result.replace(/\b(\d+)\s*paket\b/g, `$1 ${t.term_unit_pack}`);

	return result;
}

// ─── Dimension string translation (MaterialRow.dimension) ──

/**
 * Translate a MaterialRow.dimension string.
 *
 * Handles: "(110mm täck)" → "(110mm cover)", "12 mm skiva" → "12 mm board"
 */
export function translateDimension(raw: string, t: Dictionary): string {
	let result = raw;
	result = result.replace(/\btäck\b/g, t.term_dim_cover as string);
	result = result.replace(/\bskiva\b/g, t.term_dim_board as string);
	result = result.replace(/\bluftspalt\b/g, t.term_dim_airGap as string);
	// "100×200 mm K-virke" (the barrel's cradle beam). ASCII-only, so the å/ä/ö
	// sweep cannot see it — covered by an expected-string test instead.
	result = result.replace(/K-virke/g, t.term_dim_structuralTimber as string);
	// Insulation dimension "Vägg 95mm / Tak 145mm / Golv 95mm". "Tak"/"Golv"
	// are pure ASCII, so the å/ä/ö leak sweep can NOT see them — they need
	// explicit expected-string coverage, not just this replace.
	result = result.replace(/\bVägg\b/g, t.term_qty_wall as string);
	result = result.replace(/\bTak\b/g, t.term_qty_roof as string);
	result = result.replace(/\bGolv\b/g, t.term_qty_floor as string);
	// Ventilation dimension "Tilluft + Frånluft" (either casing)
	result = result.replace(
		/Tilluft \+ [Ff]rånluft/g,
		t.term_qty_supplyExhaust as string,
	);
	// Facade-treatment dimension: "2 strykningar, 4.5 m²/liter" (issue 134).
	// One anchored pattern per plural form, so neither the count nor the
	// coverage figure has to be re-derived at the render boundary.
	const coatsMatch = result.match(
		/^(\d+) strykning(ar)?, ([\d.,]+) m²\/liter$/,
	);
	if (coatsMatch) {
		const template = coatsMatch[2]
			? (t.term_dim_treatmentCoatsPlural as string)
			: (t.term_dim_treatmentCoatsSingle as string);
		return template
			.replace("{coats}", coatsMatch[1])
			.replace("{coverage}", coatsMatch[3]);
	}
	if (result === "1-lag tätskikt")
		return t.term_dim_singleLayerMembrane as string;
	if (result === "Lackerad plåt") return t.term_dim_lacqueredSheet as string;
	if (result === "Prefalsad/plåt") return t.term_dim_prefoldedSheet as string;
	// Full dimension string translations
	if (result === "IP-klassad / värmetålig") return t.term_dim_saunaFixture as string;
	if (result === "Grundinfästning")
		return t.term_dim_foundationBracket as string;
	if (result === "Geotextil") return t.term_dim_geotextile as string;
	if (result === "Takunderlag") return t.term_dim_roofUnderlay as string;
	if (result === "IP-klassad / värmetålig")
		return t.term_dim_ipRatedHeatResistant as string;
	if (result === "Klammer, dosor och skyddsrör")
		return t.term_dim_clipsBoxesConduit as string;
	if (result === "Varmzonsel") return t.term_dim_hotZoneWiring as string;
	if (result === "Grundlager") return t.term_dim_baseLayer as string;
	if (result === "Stomme och invändig infästning")
		return t.term_dim_frameInteriorFastening as string;
	if (result === "Fasad och utsatta träytor")
		return t.term_dim_facadeExposedTimber as string;
	if (result === "Band, beslag och infästning")
		return t.term_dim_bandsHardwareFastening as string;
	if (result === "Extern/integrerad")
		return t.term_dim_externalIntegrated as string;
	// === Added: 7 previously unmatched dimension strings ===
	if (result === "Vedeldad kamin") return t.term_dim_woodBurningStove as string;
	if (result === "1220×1000×50 mm, båda sidor om genomföringen")
		return t.term_dim_fireBoardBothFaces as string;
	if (result === "Obrännbar stenull A1 runt tunneln")
		return t.term_dim_feedTunnelSeal as string;
	if (result === "Härdat glas / träknopp")
		return t.term_dim_hardenedGlassDoor as string;
	if (result === "Mellansegment") return t.term_dim_midSegment as string;
	if (result === "Invändig glasdörr")
		return t.term_dim_interiorGlassDoor as string;
	if (result === "Isolerglas relaxdel")
		return t.term_dim_insulatedGlassLounge as string;
	if (result === "Isolerglas") return t.term_dim_insulatedGlass as string;
	if (result === "Härdat isolerglas (6H + 6H)")
		return t.term_dim_temperedInsulatedGlass as string;
	// GLULAM_PLACEHOLDER_DIMENSION_LABEL in engines/roofJoistSizing (issue 170).
	if (
		result ===
		"Dimensioneras av konstruktör (ritad som 45×220, riktpris limträ 56×225)"
	)
		return t.term_dim_glulamPlaceholder as string;
	if (result === "Skyddsplåt vid aggregat")
		return t.term_dim_heaterShieldPlate as string;
	if (result === "Entré / ytterdörr") return t.term_dim_entranceDoor as string;
	if (result === "Aluminium") return t.term_dim_aluminium as string;
	if (result === "Vindskyddsskiva / diffusionsöppen duk")
		return t.term_dim_windBarrier as string;
	// Trossbottenskiva. `asfaboard` carries no å/ä/ö so the Swedish-leak guard
	// cannot see it — it still needs a real English rendering.
	if (result === "12 mm asfaboard") return t.term_dim_asphaltBoard as string;
	return result;
}

// ─── Count string translation (CutRow.count) ───────────────

/**
 * Translate a CutRow.count string.
 *
 * Handles: "12 st" → "12 pcs", "1 sats" → "1 set", "45 lpm" → "45 lm"
 */
export function translateCutCount(raw: string, t: Dictionary): string {
	let result = raw;
	result = result.replace(/\b(\d+)\s*st\b/g, `$1 ${t.term_unit_pcs}`);
	result = result.replace(/\b(\d+)\s*sats\b/g, `$1 ${t.term_unit_set}`);
	result = result.replace(/\b(\d+)\s*lpm\b/g, `$1 ${t.term_unit_lm}`);
	return result;
}

// ─── Cut-length description translation (CutRow.cutLength) ──

/**
 * Translate a cut-length description.
 *
 * Handles Swedish construction phrases in cut-length details:
 * - "per takfall" → "per roof slope"
 * - "Kapas på plats" → "Cut on site"
 * - "gavlar" → "gables"
 * - etc.
 */
export function translateCutLength(raw: string, t: Dictionary): string {
	let result = raw;

	// Full phrase: "Löpande mot vägg/takvinkel, cirka N lpm" (panorama trim run)
	result = result.replace(
		/Löpande mot vägg\/takvinkel, cirka (\d+)\s*lpm/g,
		(_, count) =>
			(t.term_cut_runningWallAngle as string).replace("{count}", count),
	);

	// Lying interior panel: "Fram/bak 2500 mm, sidor 2000 mm" (issue 134).
	result = result.replace(
		/Fram\/bak (\d+) mm, sidor (\d+) mm/g,
		(_, width, length) =>
			(t.term_cut_interiorPanelLyingRuns as string)
				.replace("{width}", width)
				.replace("{length}", length),
	);

	// "spill" → "waste"
	result = result.replace(/\bspill\b/g, t.term_waste as string);

	// Indoor ceiling joist (issue 156): "1800 mm (innertaksbjälke, inget överhäng)"
	result = result.replace(
		/innertaksbjälke, inget överhäng/g,
		t.term_cut_ceilingJoistNoOverhang as string,
	);
	// "Inkl. överhäng" → "Incl. overhang"
	result = result.replace(
		/Inkl\.\s*överhäng/g,
		`${t.term_qty_incl} ${t.term_cut_overhang}`,
	);
	// "Inkl. överlapp" → "Incl. overlap"
	result = result.replace(
		/Inkl\.\s*överlapp/g,
		`${t.term_qty_incl} ${t.term_cut_overlap}`,
	);

	// "per takfall" → "per roof slope"
	result = result.replace(
		/\bper takfall\b/g,
		t.term_cut_perRoofSlope as string,
	);

	// "Kapas med" → "Cut at" (before generic "Kapas på plats" to avoid overlap)
	result = result.replace(/\bKapas med\b/g, t.term_cut_cutAt as string);

	// "Skivkapas på plats till" → "Panel-cut on site to"
	result = result.replace(
		/\bSkivkapas på plats till\b/g,
		t.term_cut_panelCutOnSite as string,
	);

	// "Kapas på plats" → "Cut on site" (generic)
	result = result.replace(
		/\bKapas på plats\b/g,
		t.term_cut_cutOnSite as string,
	);

	// "gaveltillskärning" → "gable cut" (before "gavel*" group)
	result = result.replace(
		/\bgaveltillskärning\b/g,
		t.term_cut_gableCut as string,
	);
	// "gavelspets" → "gable peak"
	result = result.replace(/\bgavelspets\b/g, t.term_cut_gablePeak as string);
	// "gavelsidor plana" → "gable sides flat"
	result = result.replace(
		/\bgavelsidor plana\b/g,
		t.term_cut_gableSidesFlat as string,
	);
	// "gavlar" → "gables"
	result = result.replace(/\bgavlar\b/g, t.term_cut_gables as string);

	// "snitt" → "cut" (angle cut)
	result = result.replace(/\bsnitt\b/g, t.term_cut_angleCut as string);

	// "stigande längd från bak till fram" → "ascending length from back to front"
	result = result.replace(
		/stigande längd från bak till fram/g,
		t.term_cut_ascendingLength as string,
	);

	// "nocktak" → "ridge roof"
	result = result.replace(/\bnocktak\b/g, t.term_cut_ridgeRoof as string);
	// "pulpettak" → "shed roof"
	result = result.replace(/\bpulpettak\b/g, t.term_cut_shedRoof as string);

	// "sidor trapetskapas och front byggs upp för" → "sides tapered and front built up for"
	result = result.replace(
		/sidor trapetskapas och front byggs upp för/g,
		t.term_cut_sidesTapered as string,
	);

	// "mot taklutning" → "to match roof pitch"
	result = result.replace(
		/\bmot taklutning\b/g,
		t.term_cut_matchRoofPitch as string,
	);

	// "Cirkelsågad" → "Circular-sawn"
	result = result.replace(
		/\bCirkelsågad\b/g,
		t.term_cut_circularSawn as string,
	);
	// "Profilsågad" → "Profile-cut"
	result = result.replace(/\bProfilsågad\b/g, t.term_cut_profileCut as string);

	// "hörn" → "corners"
	result = result.replace(/\bhörn\b/g, t.term_cut_corners as string);
	// "dörr" at the end or standalone in cut instructions
	result = result.replace(/\bdörr\b/g, t.term_cut_door as string);
	// "runt" → "around"
	result = result.replace(/\brunt\b/g, t.term_cut_around as string);
	// "följer" → "follows"
	result = result.replace(/\bföljer\b/g, t.term_cut_follows as string);
	// "och" → "and" in cut descriptions
	result = result.replace(/\boch\b/g, t.term_cut_and as string);

	// "hammarband" → "top plate" (standalone in cut length)
	result = result.replace(/\bhammarband\b/g, t.term_cut_topPlate as string);
	// "syll" → "sill plate" (standalone in cut length)
	result = result.replace(/\bsyll\b/g, t.term_cut_sillPlate as string);

	return result;
}

// ─── Electrical BOM translation ─────────────────────────────

/**
 * Electrical component name mapping.
 * Maps the Swedish component names from getDefaultElectricalComponents
 * to dictionary keys.
 */
const ELECTRICAL_ITEM_MAP: Array<[RegExp, DictKey]> = [
	[/^Takspots \(infälld belysning\)/, "term_el_ceilingSpots"],
	[/^Vägglampa \(hög\)/, "term_el_wallLightHigh"],
	[/^Bastuaggregat anslutning/, "term_el_heaterConnection"],
	[/^LED-strip \(låg, under lav\)/, "term_el_ledStripLow"],
];

/**
 * Translate an electrical BOM item string.
 *
 * Electrical items from getDefaultElectricalComponents use Swedish names
 * followed by " Kabel" suffix. This translates both parts.
 */
export function translateElectricalItem(raw: string, t: Dictionary): string {
	// Strip " Kabel" suffix, translate the component name, re-add translated suffix
	const kabelSuffix = " Kabel";
	const hasKabelSuffix = raw.endsWith(kabelSuffix);
	const coreName = hasKabelSuffix ? raw.slice(0, -kabelSuffix.length) : raw;

	for (const [pattern, key] of ELECTRICAL_ITEM_MAP) {
		if (pattern.test(coreName)) {
			const translated = t[key] as string;
			return hasKabelSuffix ? `${translated} ${t.term_el_cable}` : translated;
		}
	}

	// Fallback: try replacing just the " Kabel" suffix
	if (hasKabelSuffix) {
		return `${coreName} ${t.term_el_cable}`;
	}
	return raw;
}

/**
 * Translate an electrical cable type string from rule registry defaults.
 */
export function translateElectricalCable(raw: string, t: Dictionary): string {
	if (/^Värmebeständig Silikonkabel/.test(raw)) {
		return t.term_el_siliconeCable as string;
	}
	if (/^Standard Kabel/.test(raw)) {
		return t.term_el_standardCable as string;
	}
	return raw;
}

/**
 * Translate an electrical warning string from rule registry defaults.
 */
export function translateElectricalWarning(raw: string, t: Dictionary): string {
	if (/^LIVSFARA/.test(raw)) {
		return t.term_el_hotZoneWarning as string;
	}
	if (/^Placera ALDRIG/.test(raw)) {
		return t.term_el_transformerWarning as string;
	}
	return raw;
}

